/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import constants from '../../constants';
import { lookupContextValue } from '../../store/state/state-manager';
import { createC11nEnv } from '../c11n-env';
import type { Meta, Options } from '../types';

import RepeatDataIterator from './repeat-data';

class ChildrenIterator {
  private readonly children: any;
  private readonly options: Options;
  private index: number;

  constructor(children: any, options: Options) {
    this.children = children;
    this.index = 0;
    this.options = options;
  }

  hasNext() {
    return this.index < this.children.length;
  }

  next() {
    if (!this.hasNext()) {
      throw new Error('No Children');
    }
    const child = this.children[this.index];
    this.index += 1;

    return createC11nEnv({
      meta: child,
      index: this.index,
      options: this.options
    });
  }

  resetIndex() {
    this.index = 0;
  }
}

const getChildrenIterator = (meta: Meta | undefined, options: Options) => {
  const repeat = !!meta?.repeat;
  const repeatSelf = !!meta?.['repeat-self'];
  if (repeatSelf) {
    return new RepeatDataIterator(meta, meta.context || '', options);
  }
  if (repeat) {
    const pageListContext = lookupContextValue(
      `${options.context}.${constants.SUMMARY_OF_LISTS}`,
      meta.pageReference,
      options.context
    );
    return new RepeatDataIterator(meta.children[0], pageListContext || meta.context, options);
  }

  if (meta?.children) {
    return new ChildrenIterator(meta.children, options);
  }
};

export default getChildrenIterator;
